import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { formatInteger, formatMilliseconds } from '../utils/formatters';
import { getStatusModifier, formatStatusLabel } from '../utils/status';
import { useServiceMetricSummaries } from '../hooks/useServiceMetricSummaries';

const Section = ({ title, children, className }) => (
    <section className={["service-health-card__section", className].filter(Boolean).join(' ')}>
        {title && <h4 className="service-health-card__section-title">{title}</h4>}
        {children}
    </section>
);

Section.propTypes = {
    title: PropTypes.node,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
};

Section.defaultProps = {
    title: null,
    className: '',
};

export const serviceHealthPropType = PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    status: PropTypes.string,
    observedAt: PropTypes.string,
    components: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        status: PropTypes.string,
    })),
    metrics: PropTypes.shape({
        uptimeSeconds: PropTypes.number,
        cpuUsage: PropTypes.number,
        systemLoad1m: PropTypes.number,
        memory: PropTypes.shape({
            usedBytes: PropTypes.number,
            maxBytes: PropTypes.number,
        }),
        requests: PropTypes.shape({
            totalCount: PropTypes.number,
            errorCount: PropTypes.number,
            averageResponseMs: PropTypes.number,
            endpointSamples: PropTypes.arrayOf(PropTypes.shape({
                uri: PropTypes.string.isRequired,
                count: PropTypes.number,
                averageResponseMs: PropTypes.number,
                maxResponseMs: PropTypes.number,
            })),
        }),
    }),
    notes: PropTypes.arrayOf(PropTypes.string),
});

const ServiceHealthCard = ({ service, onSelect, isSelected }) => {
    const {
        id,
        name,
        status,
        observedAt,
        components = [],
        metrics,
        notes = [],
    } = service;

    const observed = observedAt ? new Date(observedAt) : null;

    const {
        uptime,
        cpuUsage,
        load,
        memoryUsed,
        memoryMax,
        totalRequests,
        errorCount,
        avgLatency,
        endpointSamples,
    } = useServiceMetricSummaries(metrics);

    const statusModifier = getStatusModifier(status);
    const statusClassName = `service-health-card__status-dot--${statusModifier}`;
    const statusLabel = formatStatusLabel(status);

    const handleSelect = useCallback(() => {
        if (onSelect) {
            onSelect(id);
        }
    }, [id, onSelect]);

    const handleKeyDown = useCallback((event) => {
        if (!onSelect) {
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(id);
        }
    }, [id, onSelect]);

    const classNames = ['service-health-card'];
    if (onSelect) {
        classNames.push('service-health-card--selectable');
    }
    if (isSelected) {
        classNames.push('service-health-card--selected');
    }

    const interactiveProps = onSelect ? {
        role: 'button',
        tabIndex: 0,
        onClick: handleSelect,
        onKeyDown: handleKeyDown,
        'aria-pressed': isSelected,
        'aria-label': `${name || id} status ${statusLabel}`,
    } : {};

    return (
        <article className={classNames.join(' ')} {...interactiveProps}>
            <header className="service-health-card__header">
                <div className="service-health-card__heading stack stack--sm">
                    <h3 className="service-health-card__title">{name}</h3>
                    <div className="service-health-card__status">
                        <span className={`service-health-card__status-dot ${statusClassName}`} aria-hidden="true" />
                        <span className="service-health-card__status-text">{statusLabel}</span>
                    </div>
                </div>
                {observed && (
                    <time className="service-health-card__observed" dateTime={observed.toISOString()}>
                        Observed {observed.toLocaleTimeString()}
                    </time>
                )}
            </header>

            <Section className="service-health-card__metrics">
                <MetricRow label="Uptime" value={uptime} />
                <MetricRow label="CPU Usage" value={cpuUsage} />
                <MetricRow label="Load (1m)" value={load} />
                <MetricRow label="Memory" value={memoryUsed && memoryMax ? `${memoryUsed} / ${memoryMax}` : memoryUsed || 'N/A'} />
                <MetricRow label="Requests" value={totalRequests} />
                <MetricRow label="Errors" value={errorCount} />
                <MetricRow label="Avg Latency" value={avgLatency} />
            </Section>

            {components.length > 0 && (
                <Section className="service-health-card__components" title="Components">
                    <ul className="service-health-card__list">
                        {components.map((component) => {
                            const componentModifier = getStatusModifier(component.status);
                            return (
                                <li key={component.name} className="service-health-card__list-item">
                                    <span className="service-health-card__list-label">{component.name}</span>
                                    <span className={`service-health-card__chip service-health-card__chip--${componentModifier}`}>
                                        {formatStatusLabel(component.status)}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </Section>
            )}

            {endpointSamples.length > 0 && (
                <Section className="service-health-card__endpoints" title="Endpoint Samples">
                    <ul className="service-health-card__list">
                        {endpointSamples.map((endpoint) => (
                            <li key={endpoint.uri} className="service-health-card__endpoint">
                                <div className="service-health-card__endpoint-meta">
                                    <span className="service-health-card__endpoint-uri">{endpoint.uri}</span>
                                    <span className="service-health-card__endpoint-count">{formatInteger(endpoint.count)} calls</span>
                                </div>
                                <div className="service-health-card__endpoint-metrics">
                                    <span>Avg {formatMilliseconds(endpoint.averageResponseMs)}</span>
                                    {endpoint.maxResponseMs !== null && endpoint.maxResponseMs !== undefined && (
                                        <span>Max {formatMilliseconds(endpoint.maxResponseMs)}</span>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {notes.length > 0 && (
                <Section className="service-health-card__notes" title="Notes">
                    <ul className="service-health-card__list">
                        {notes.map((note, idx) => (
                            <li key={`${id}-note-${idx}`} className="service-health-card__note">
                                {note}
                            </li>
                        ))}
                    </ul>
                </Section>
            )}
        </article>
    );
};

const MetricRow = ({ label, value }) => (
    <div className="service-health-card__metric">
        <span className="service-health-card__metric-label">{label}</span>
        <span className="service-health-card__metric-value">{value || 'N/A'}</span>
    </div>
);

MetricRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
};

MetricRow.defaultProps = {
    value: null,
};

ServiceHealthCard.propTypes = {
    service: serviceHealthPropType.isRequired,
    onSelect: PropTypes.func,
    isSelected: PropTypes.bool,
};

ServiceHealthCard.defaultProps = {
    onSelect: undefined,
    isSelected: false,
};

export default ServiceHealthCard;
